import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { environment } from '../../../../environments/environment'
import { ApiHttpService } from '../providers/api-http.service'
import { CookieService } from 'ngx-cookie-service'
import { UuidServiceService } from './uuid-service'

@Injectable({
  providedIn: 'root'
})
export class WIRService extends ApiHttpService {
  private academicOptionsStore = new BehaviorSubject<any>({})

  constructor (
    public override http: HttpClient,
    public cookieService: CookieService,
    public uuid: UuidServiceService
  ) {
    super(http)
  }

  private getAuthToken (): string {
    return this.cookieService.get('wircognito-new-access-token') || this.cookieService.get('wircognito-access-token')
  }

  private getRefreshToken (): string {
    return this.cookieService.get('wircognito-refresh')
  }

  getSfAPI (counsellingMode: any): any {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: this.getAuthToken(),
        'Refresh-Token': this.getRefreshToken(),
        'X-Correlation-ID': this.uuid.uuidv4()
      }),
      params: new HttpParams({
        fromString: 'counsellingMode=' + counsellingMode
      })
    }
    const endpoint = environment.sfAPI
    return this.get(endpoint, options).pipe(map((result) => result))
  }

  isUserAlreadyExist (email: any, language: any): any {
    const sfDetails: any = localStorage.getItem('sfResponse')
    if (!sfDetails) {
      return new Observable(observer => {
        observer.error(new Error('sfResponse not found in localStorage'))
        observer.complete()
      })
    }
    const officeDetails: any = JSON.parse(sfDetails)
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: this.getAuthToken(),
        'Refresh-Token': this.getRefreshToken(),
        'X-Correlation-ID': this.uuid.uuidv4()
      }),
      params: new HttpParams({
        fromString: 'studentEmail=' + email + '&lang=' + language +
          '&staff_office_country=' + officeDetails.wir_country_code +
          '&counsellor_mode=' + officeDetails.wir_cognito_counsellor_mode +
          '&staff_office_team=' + officeDetails.wir_counsellor_team +
          '&counsellorEmail=' + officeDetails.counsellor_email
      })
    }
    const endpoint = environment.isUserExistAPI
    return this.get(endpoint, options).pipe(map((result) => result))
  }

  userRegistrationForm (userDetail: any): any {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: this.getAuthToken(),
        'Refresh-Token': this.getRefreshToken(),
        'X-Correlation-ID': this.uuid.uuidv4()
      })
    }
    const endpoint = environment.postPPRegistration
    return this.post(endpoint, userDetail, options).pipe(map((result) => result))
  }

  getReferencedValues (payload: any): any {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'X-Correlation-ID': this.uuid.uuidv4(),
      Authorization: this.getAuthToken(),
      'Refresh-Token': this.getRefreshToken()
    })

    let params = new HttpParams().set('country_3_code', payload.country_3_code).set('source', 'wir')
    if (payload.country_qual_id) {
      params = params.set('country_qual_id', payload.country_qual_id)
    }
    if (payload.qual_exam_board_id) {
      params = params.set('qual_exam_board_id', payload.qual_exam_board_id)
    }
    if (payload.grade_id) {
      params = params.set('grade_id', payload.grade_id)
    }

    const endpoint = environment.referenceDataValuesAPI
    return this.http.get(endpoint, { headers, params }).pipe(map(result => {
      this.storeAcademicOptions(payload, result)
      return result
    }))
  }

  private storeAcademicOptions (payload: any, response: any): void {
    const currentStore = this.academicOptionsStore.value
    const key = this.generateStoreKey(payload)
    currentStore[key] = response
    this.academicOptionsStore.next(currentStore)
  }

  private generateStoreKey (payload: any): string {
    const parts = [payload.country_3_code]
    if (payload.country_qual_id) parts.push(payload.country_qual_id)
    if (payload.qual_exam_board_id) parts.push(payload.qual_exam_board_id)
    if (payload.grade_id) parts.push(payload.grade_id)
    return parts.join('_')
  }

  getStoredAcademicOptions (payload: any): any {
    const key = this.generateStoreKey(payload)
    return this.academicOptionsStore.value[key] || null
  }

  clearAcademicOptionsStore (): void {
    this.academicOptionsStore.next({})
  }

  clearAcademicOptionsByCountry (countryCode: string): void {
    const currentStore = this.academicOptionsStore.value
    const keysToDelete = Object.keys(currentStore).filter(key => key.startsWith(countryCode))
    keysToDelete.forEach(key => delete currentStore[key])
    this.academicOptionsStore.next(currentStore)
  }

  clearAcademicOptionsByPayload (payload: any): void {
    const key = this.generateStoreKey(payload)
    const currentStore = this.academicOptionsStore.value
    const keysToDelete = Object.keys(currentStore).filter(storeKey => storeKey.startsWith(key))
    keysToDelete.forEach(k => delete currentStore[k])
    this.academicOptionsStore.next(currentStore)
  }
}
